import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, vehicleLabel } from "@/lib/format";
import { MagnifyingGlass, X as XIcon, Receipt, Car, Calendar, ShieldCheck, ArrowsLeftRight, CurrencyInr, Bookmark, ArrowUUpLeft } from "@phosphor-icons/react";

/**
 * Global VIN / customer lifecycle lookup.
 *
 * One input box that accepts either a VIN (chassis) OR a customer phone (any
 * length; we strip non-digits and match on the last 10). Hits
 * `GET /api/lifecycle` and renders a chronological timeline of every event
 * tied to the matched vehicle / customer.
 *
 * Deliberately permissionless (any authenticated user can search) per product
 * requirement — floor staff, accounts, admins all need to trace a unit.
 */

const KIND_META = {
  vehicle_created: { icon: Car, cls: "border-ink", label: "INVENTORY" },
  branch_transfer: { icon: ArrowsLeftRight, cls: "border-[#1971c2] text-[#1971c2]", label: "TRANSFER" },
  customer_ref: { icon: Bookmark, cls: "border-ink-muted text-ink-muted", label: "CUSTOMER" },
  booking_created: { icon: Calendar, cls: "border-[#5f3dc4] text-[#5f3dc4]", label: "BOOKING" },
  booking_payment: { icon: CurrencyInr, cls: "border-[#5f3dc4] text-[#5f3dc4]", label: "BKG PAY" },
  sale_created: { icon: Receipt, cls: "border-[#00713a] text-[#00713a]", label: "SALE" },
  sale_reconcile: { icon: CurrencyInr, cls: "border-[#E08600] text-[#A35700]", label: "RECONCILE" },
  ad_invoice: { icon: Receipt, cls: "border-[#002FA7] text-[#002FA7]", label: "AD INVOICE" },
  ad_invoice_return: { icon: ArrowUUpLeft, cls: "border-[#A35700] text-[#A35700]", label: "AD RETURN" },
  ad_invoice_cancelled: { icon: XIcon, cls: "border-destructive text-destructive", label: "AD CANCELLED" },
  registration_created: { icon: ShieldCheck, cls: "border-[#1971c2] text-[#1971c2]", label: "REG OPENED" },
  registration_stage: { icon: ShieldCheck, cls: "border-[#1971c2] text-[#1971c2]", label: "REG STAGE" },
  accessory_issued: { icon: Bookmark, cls: "border-[#6f42c1] text-[#6f42c1]", label: "ACCESSORY" },
};

function EventCard({ event }) {
  const meta = KIND_META[event.kind] || { icon: Receipt, cls: "border-canvas-line", label: event.kind.toUpperCase() };
  const Icon = meta.icon;
  return (
    <div className={`border ${meta.cls} bg-canvas-card p-3 flex items-start gap-3`}>
      <Icon size={14} weight="bold" className="mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`inline-block px-1.5 py-0.5 border ${meta.cls} text-[9px] font-mono uppercase tracking-wider`}>{meta.label}</span>
          <span className="font-mono text-[10px] text-ink-muted">{(event.when || "").slice(0, 10) || "—"}</span>
        </div>
        <div className="text-sm text-ink">{event.title}</div>
        {event.receipt_no && <div className="font-mono text-[10px] text-ink-muted mt-0.5">Receipt {event.receipt_no}</div>}
      </div>
    </div>
  );
}

export default function LifecycleWidget() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const search = async (e) => {
    e?.preventDefault?.();
    const q = (query || "").trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      // Heuristic — if it's mostly digits and ≤13 chars, treat as a phone.
      // Otherwise treat as a VIN.
      const digits = q.replace(/\D/g, "");
      const params = digits && digits.length >= 4 && digits.length <= 13 && digits.length === q.replace(/[\s+\-()]/g, "").length
        ? { phone: q }
        : { vin: q.toUpperCase() };
      const { data } = await api.get("/lifecycle", { params });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setQuery(""); setResult(null); };

  return (
    <div className="border border-canvas-line bg-canvas-card p-5 space-y-4" data-testid="lifecycle-widget">
      <div className="flex items-center gap-2">
        <MagnifyingGlass size={14} weight="bold" className="text-ink-muted" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">VIN / Customer Lifecycle</div>
      </div>
      <form onSubmit={search} className="flex items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter VIN or customer phone…"
          data-testid="lifecycle-input"
          className="flex-1 px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <button type="submit" disabled={loading || !query.trim()} data-testid="lifecycle-search" className="px-4 py-2 bg-ink text-white text-xs font-semibold disabled:opacity-50">
          {loading ? "Searching…" : "Trace"}
        </button>
        {result && (
          <button type="button" onClick={reset} data-testid="lifecycle-reset" className="px-3 py-2 border border-canvas-line text-xs">Clear</button>
        )}
      </form>

      {result && (
        <div className="space-y-3">
          {result.vehicle && (
            <div className="border border-canvas-line bg-canvas-panel p-3 text-xs space-y-1" data-testid="lifecycle-vehicle-card">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Vehicle</div>
              <div className="font-semibold text-sm">
                {[vehicleLabel(result.vehicle), result.vehicle.color].filter(Boolean).join(" · ")}
              </div>
              <div className="font-mono text-[11px] text-ink-muted">
                VIN {result.vehicle.vin || "—"} · Status {result.vehicle.status || "—"} · Branch {result.vehicle.branch_id || "—"}
                {result.vehicle.on_road_price ? ` · ${fmtMoney(result.vehicle.on_road_price)}` : ""}
              </div>
            </div>
          )}
          {result.customers_matched > 0 && (
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted" data-testid="lifecycle-customer-count">
              {result.customers_matched} customer record{result.customers_matched > 1 ? "s" : ""} matched by phone
            </div>
          )}

          {result.events.length === 0 ? (
            <div className="text-center py-8 font-mono text-xs text-ink-subtle border border-canvas-line">
              No events recorded for this lookup.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="lifecycle-events">
              {result.events.map((e, i) => (
                <EventCard key={`${e.kind}-${e.when}-${i}`} event={e} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
